import type { ComponentProps, FC } from 'react';
import React, { memo, useContext } from 'react';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import Box from '@atlaskit/primitives/box';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { xcss } from '@atlaskit/primitives/xcss';

import { fg } from '@confluence/feature-gating';
import { ReadTime } from '@confluence/read-time/entry-points/ReadTime.ts';
import { AnalyticsByLine } from '@confluence/confluence-analytics/entry-points/AnalyticsByLine.ts';
import { ExternalShareContext } from '@confluence/external-share-context/entry-points/ExternalShareContext.ts';
import { BadgeStylePageOwnershipByline } from '@confluence/content-ownership/entry-points/BadgeStylePageOwnershipByline.ts';
import { AutoConversionByLine } from '@confluence/editor-conversion/entry-points/AutoConversionByLine.ts';
import {
	TitleAlignmentType,
	type TitleContentPropertiesType,
} from '@confluence/content-topper/entry-points/contentTopperConstants.ts';
import { EditorContentState } from '@confluence/content-state/entry-points/EditorContentState.ts';
import { RendererContentState } from '@confluence/content-state/entry-points/RendererContentState.ts';
import { ViewPageBylineReactions } from '@confluence/reactions/entry-points/ViewPageBylineReactions.ts';
import { useCanPerformContentOperation } from '@confluence/entity-operations/entry-points/entityOperationsHelpers.ts';
import type { TargetContentType } from '@confluence/entity-operations/entry-points/operationsTypes.ts';
import { OperationTypes } from '@confluence/entity-operations/entry-points/operationsTypes.ts';
import { CURRENT_STATUS } from '@confluence/page-utils/entry-points/enums.ts';
import { useContentType } from '@confluence/page-context';
import { expValEquals, isExperimentEnabled } from '@confluence/feature-experiments';
import { ListenByline } from '@confluence/audio/entry-points/ListenByline.ts';
import { useAudioFeatureFlags } from '@confluence/audio/entry-points/useAudioFeatureFlags.ts';
import {
	BylineConnections,
	type DevDocsBylineConnectionAncestor,
} from '@confluence/byline-connections/entry-points/BylineConnections.ts';
import { AgentTrustReceiptBylineChip } from '@confluence/agent-trust-receipt/entry-points/AgentTrustReceiptBylineChip.ts';
import { useIsEmbeddedConfluence_DO_NOT_USE } from '@confluence/embedded-confluence-check';
import type { useContentClassification_content$key } from '@confluence/data-classification/entry-points/useContentClassificationFragmentType.ts';

import type { UserShape } from './byLineTypes.ts';
import { SingleLineByLine } from './SingleLineByLine.tsx';
import { StyledMainDiv, StyledTextDiv } from './PresentationalComponents.tsx';
import { ByLineLastUpdatedCompact } from './ByLineLastUpdatedCompact.tsx';
import { BylineClassification } from './BylineClassification.tsx';
import { LazyAIPlannerSyncByline } from './ai-planner-sync/AIPlannerSyncBylineLoader.tsx';
import { AI_PLANNER_GENERATED_BY } from './ai-planner-sync/aiPlannerSyncBylineConfig.ts';

type version = {
	friendlyWhen: string;
	when: string | null | undefined;
	number: number;
	by: UserShape;
	contentTypeModified: boolean;
};

type ByLineProps = {
	contentId: string;
	author: UserShape;
	owner: UserShape | null;
	version: version;
	contributorsCount: number;
	isOwnerContributor: boolean;
	hasByLineContributors?: boolean;
	hasByLineExtensions?: boolean;
	isFabricPage: boolean;
	titleContentProperties: TitleContentPropertiesType;
	shouldShowBylineReactions?: boolean;
	contentType?: string | null;
	spaceId?: string | null;
	contentSubType?: string | null;
	generatedBy?: string | null;
	connectionAncestors?: readonly DevDocsBylineConnectionAncestor[] | null;
	createdDate?: string | null;
	contentFragmentRef?: unknown;
	bylineClassificationFragmentRef?: ComponentProps<
		typeof BylineClassification
	>['classificationContextFragmentRef'];
	contentClassificationFragmentRef?: useContentClassification_content$key | null;
	reactionsContentFragmentRef?: ComponentProps<
		typeof ViewPageBylineReactions
	>['reactionsContentFragmentRef'];
};

const bylineLastUpdatedStyles = xcss({
	marginTop: 'space.negative.025',
	marginLeft: 'space.negative.050',
	marginRight: 'space.negative.075',
});

const ReadTimeWithContentFragmentRef = ReadTime as FC<{
	contentId: string;
	contentFragmentRef?: unknown;
}>;

export const ByLineWithContributorsComponent: FC<ByLineProps> = memo(
	({
		contentId,
		author,
		owner,
		version,
		contributorsCount,
		isOwnerContributor,
		hasByLineContributors,
		hasByLineExtensions,
		isFabricPage,
		titleContentProperties,
		shouldShowBylineReactions,
		contentType,
		contentSubType,
		spaceId,
		generatedBy,
		connectionAncestors,
		createdDate,
		contentFragmentRef,
		bylineClassificationFragmentRef,
		contentClassificationFragmentRef,
		reactionsContentFragmentRef,
	}) => {
		const { isExternalShareRequest } = useContext(ExternalShareContext);
		const [contentTypeFromContext] = useContentType();
		const { isAudioBylineEntrypointEnabled } = useAudioFeatureFlags();
		const canUpdateContent = useCanPerformContentOperation({
			contentId,
			contentType: (contentType ?? contentTypeFromContext) as TargetContentType,
			operation: OperationTypes.UPDATE,
			status: CURRENT_STATUS,
		});
		const hasEditPermission = canUpdateContent ?? false;
		const isEmbeddedConfluence = useIsEmbeddedConfluence_DO_NOT_USE();

		const isTitleCenterAligned =
			titleContentProperties?.titleLayoutAlignment == TitleAlignmentType.CENTER;
		const showByLineContributors = hasByLineContributors ?? true;
		const showByLineExtensions = hasByLineExtensions ?? true;
		const shouldRenderAIPlannerSyncByline =
			generatedBy === AI_PLANNER_GENERATED_BY && fg('confluence_ai_planner_sync_byline');

		return (
			<StyledMainDiv
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop
				className="page-metadata-modification-info"
				data-testid="page-main-div"
				isTitleCenterAligned={isTitleCenterAligned}
			>
				<StyledTextDiv hasBadgeStylePageOwnerBylineItem>
					<SingleLineByLine
						contentId={contentId}
						isTitleCenterAlignedOverride={isTitleCenterAligned}
						showByLineExtensions={showByLineExtensions}
					>
						{showByLineContributors && (
							<>
								<BadgeStylePageOwnershipByline
									owner={owner}
									author={author}
									analyticsSource="renderer"
									contributorCountWithoutOwner={contributorsCount - (isOwnerContributor ? 1 : 0)}
									generatedBy={generatedBy}
								/>
								<BylineClassification
									contentId={contentId}
									classificationContextFragmentRef={bylineClassificationFragmentRef}
									contentFragmentRef={contentClassificationFragmentRef}
								/>
								{expValEquals('confluence_approvals_eap', 'isEnabled', true) ? (
									<EditorContentState
										contentId={contentId}
										contentType={
											(contentType ?? contentTypeFromContext) as 'page' | 'blogpost' | null
										}
										isViewMode
										canEdit={hasEditPermission}
										userOffline={false}
									/>
								) : (
									<RendererContentState
										contentId={contentId}
										isDraft={false}
										hasSeparator={false}
									/>
								)}
								{!isFabricPage && (
									<Box xcss={bylineLastUpdatedStyles} testId="page-last-modified">
										<ByLineLastUpdatedCompact version={version} />
									</Box>
								)}
							</>
						)}
						{shouldRenderAIPlannerSyncByline && (
							<LazyAIPlannerSyncByline
								contentId={contentId}
								contentType={contentType ?? contentTypeFromContext}
								generatedBy={generatedBy}
								isEmbeddedConfluence={isEmbeddedConfluence}
							/>
						)}
						{showByLineExtensions && (
							<>
								<ReadTimeWithContentFragmentRef
									contentId={contentId}
									contentFragmentRef={contentFragmentRef}
								/>
								{fg('cc_sparks_agent_trust_receipt') && (
									<AgentTrustReceiptBylineChip
										contentId={contentId}
										generatedBy={generatedBy}
										creatorName={author?.fullName}
										createdAt={createdDate}
										lastUpdatedAt={version?.when}
										versionNumber={version?.number}
									/>
								)}
								{isExperimentEnabled('confluence_1p_and_3p_connection_byline_experiment') && (
									<BylineConnections
										contentId={contentId}
										connectionAncestors={connectionAncestors}
									/>
								)}
								{isAudioBylineEntrypointEnabled && (
									<ListenByline contentId={contentId} contentType={contentType ?? undefined} />
								)}
								<AnalyticsByLine contentId={contentId} contentAuthorId={author.userId} />
								{shouldShowBylineReactions && (
									<ViewPageBylineReactions
										contentId={contentId}
										contentType={contentType}
										spaceId={spaceId}
										contentSubType={contentSubType}
										reactionsContentFragmentRef={reactionsContentFragmentRef}
										noFlexWrap
										noRelativeContainer
									/>
								)}
								{!isExternalShareRequest && <AutoConversionByLine />}
							</>
						)}
					</SingleLineByLine>
				</StyledTextDiv>
			</StyledMainDiv>
		);
	},
);
